import { Component, computed, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { Store } from '@ngrx/store';
import { ApplicationAppEntityApplicationRead } from '../../../../../openapi';
import {
  selectApplication,
  selectLoading,
  selectPending,
  selectServiceAttachmentPositions
} from '../../../../../store/selectors/application.selectors';
import { FileUploaderComponent } from '../../../../../common/file-uploader/file-uploader.component';
import { AttachmentsForm } from './forms/attachments.form';
import { ReactiveFormsModule } from '@angular/forms';
import {
  approveApplication,
  loadServiceAttachmentPositions,
  updateApplicationAttachments
} from '../../../../../store/actions/application.actions';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { SessionService, UISettings } from '../../../../../services/ssession.service';
import { AttachmentPositions } from '../../../../../services/application.common.model';
import { selectUISettings } from '../../../../../store/selectors/app.selectors';

export interface AttachmentDataComponentConfig {
  stages: string[];
  cancelUrl: string;
}

@Component({
  selector: 'app-attachments-data-section',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    SpinnerComponent,
    FileUploaderComponent,
    ReactiveFormsModule,
    StagesComponent,
    AlertComponent
  ],
  providers: [
    AttachmentsForm
  ],
  templateUrl: './attachments-data-section.component.html'
})
export class AttachmentsDataSectionComponent implements OnInit, OnDestroy {

  public stages: string[] = this.route.snapshot.data['stages'] || [];

  public cancelUrl: string = this.route.snapshot.data['cancelUrl'];

  public uiSettings: Signal<UISettings | null> = this.store$.selectSignal(selectUISettings);

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public uploading: WritableSignal<number> = signal<number>(0);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public attachmentsPositions: Signal<AttachmentPositions> = this.store$.selectSignal(selectServiceAttachmentPositions);

  private fetchAttachmentPositions: Subscription = toObservable(this.attachmentsPositions).subscribe(v => {
    if (!!v.length) {
      this.form.buildForm(v, this.application());
    }
  });

  constructor(
    private store$: Store,
    private route: ActivatedRoute,
    private router: Router,
    public form: AttachmentsForm
  ) {
  }

  public handleUploading(v: boolean): void {
    if (v) {
      this.uploading.update(v => v + 1);
    } else {
      this.uploading.update(v => v - 1);
    }
  }

  public token: string | null = SessionService.GetAccessToken();

  public feeCategory: Signal<string> = computed(() => this.attachmentsPositions().filter(v => !!v.payment?.feeCategory).map(v => v.payment)[0]?.feeCategory?.split('/').pop() || '');

  public submitAndGoNext(): void {
    if (this.form.valid) {
      const application = this.application();
      if (application) {
        this.store$.dispatch(
          updateApplicationAttachments({
            application,
            attachments: this.form.values,
            successFnc: () => {
              this.approveApplication();
            },
            failureFnc: () => {
            }
          })
        );
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  private approveApplication(): void {
    this.store$.dispatch(
      approveApplication({
        applicationId: this.applicationId || '',
        successFnc: () => {
          if (this.uiSettings()?.mobileEmbed) {
            window.location.href = `mobile-um-wronki://application/approved/success?id=${this.applicationId}`;
          } else {
            this.router.navigate(['/wnioski-zatwierdzone' + this.application()?.service?.url, this.applicationId], { state: { message: 'applicationApproved' } });
          }
        },
        failureFnc: () => {
          if (this.uiSettings()?.mobileEmbed) {
            window.location.href = `mobile-um-wronki://application/approved/failure?id=${this.applicationId}`;
          }
        }
      })
    );
  }

  public ngOnInit(): void {
    if (this.applicationId) {
      this.store$.dispatch(loadServiceAttachmentPositions({ appId: this.applicationId }));
    }
  }

  public ngOnDestroy(): void {
    this.fetchAttachmentPositions.unsubscribe();
  }
}
